import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { InstitutionalDeskReadError, readInstitutionalDeskOrders, type InstitutionalDeskOrders } from '@/lib/polymarketUs/institutionalOrdersClient';
import type { UsInstitutionalDeskAccount } from '@/views/TraderDetail/usManagerReads';

const subscribe = (notify: () => void) => useAuthStore.subscribe(notify);
type Loaded = { key: string; data: InstitutionalDeskOrders | null; busy: boolean; failed: boolean };
const EMPTY: readonly UsInstitutionalDeskAccount[] = [];

/** No polling: mounting, account selection and the page refresh read saved
 * evidence only. The separate Refresh US action owns the venue request. */
export function useInstitutionalDeskOrders(accounts: readonly UsInstitutionalDeskAccount[] = EMPTY,
  orgKey: string, refreshKey: number) {
  const epoch = useSyncExternalStore(subscribe, getAuthSessionEpoch);
  const userId = useAuthStore(s => s.user?.id);
  const eligible = accounts.filter(a => userId !== undefined && a.deskUserIds.includes(userId));
  const [selected, setSelected] = useState('');
  const account = eligible.find(a => a.id === selected) ?? eligible[0] ?? null;
  const accountId = account?.id ?? '';
  const key = `${epoch}|${orgKey}|${accountId}`;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const current = loaded?.key === key ? loaded : null;
  const currentKey = useRef(key);
  currentKey.current = key;

  async function request(refresh?: InstitutionalDeskOrders['scope']) {
    if (!accountId || (refresh && busy.current)) return;
    controller.current?.abort();
    const own = new AbortController(); controller.current = own;
    const seq = ++sequence.current;
    busy.current = true;
    setLoaded(prev => ({ key, data: prev?.key === key ? prev.data : null, busy: true, failed: false }));
    const valid = () => seq === sequence.current && currentKey.current === key && getAuthSessionEpoch() === epoch;
    try {
      const data = await readInstitutionalDeskOrders(accountId, AbortSignal.any([own.signal, AbortSignal.timeout(20_000)]), refresh);
      if (valid()) setLoaded({ key, data, busy: false, failed: data.lastAttempt?.state === 'unavailable' });
    } catch (error) {
      if (valid()) setLoaded(prev => ({ key, busy: false, failed: true,
        data: error instanceof InstitutionalDeskReadError && error.accessChanged ? null : prev?.key === key ? prev.data : null }));
    } finally { if (valid()) busy.current = false; }
  }
  useEffect(() => {
    void request();
    // This is a request generation, not a DOM ref. Invalidate whichever GET
    // or explicit refresh belongs to the departing account/session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { sequence.current++; busy.current = false; controller.current?.abort(); };
    // Requests are keyed to the complete visible account/session context, not
    // the render's callback identity. The explicit action captures current scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshKey]);

  function invalidateAccess() {
    sequence.current++; controller.current?.abort(); busy.current = false;
    setLoaded({ key, data: null, busy: false, failed: true });
  }
  return { invalidateAccess, eligible, account, select: setSelected, data: current?.data ?? null,
    busy: account !== null && (current === null || current.busy), failed: current?.failed ?? false,
    refresh: () => { if (current?.data?.canRefresh && !current.busy) void request(current.data.scope); },
    readSaved: () => { if (!busy.current) void request(); } };
}
